import React, { createContext, useContext, useState, useEffect } from 'react';
import { getTelegramInitData } from '@/lib/telegram';
import { getWhitelistEntry } from '@leafstock/shared';
import type { User, Business, UserBusiness } from '@/types';

interface AuthContextValue {
  user: User | null;
  business: Business | null;
  userBusiness: UserBusiness | null;
  authToken: string | null;
  staffMember: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setBusiness: (business: Business | null) => void;
  setUserBusiness: (userBusiness: UserBusiness | null) => void;
  setAuthToken: (token: string | null) => void;
  setStaffMember: (staffMember: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [userBusiness, setUserBusiness] = useState<UserBusiness | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [staffMember, setStaffMemberState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for test mode (for Puppeteer/automated testing) - DEV only
    const urlParams = new URLSearchParams(window.location.search);
    const testMode = import.meta.env.DEV && urlParams.get('testMode') === 'true';

    if (testMode) {
      setAuthToken('dev-token');
      setUser({
        id: 'test-user-1',
        telegramId: '7584507876',
        firstName: 'Test',
        lastName: 'User',
        username: 'testuser',
        globalRole: 'SUPER_ADMIN' as any,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setBusiness({
        id: 'bryce-001',
        code: 'BRYCE001',
        name: 'Bryce Cannabis Co.',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setUserBusiness({
        id: 'test-ub-1',
        userId: 'test-user-1',
        businessId: 'bryce-001',
        role: 'SUPER_ADMIN' as any,
        isActive: true,
        createdAt: new Date(),
      });
      setIsLoading(false);
      return;
    }

    // Get Telegram init data as auth token
    const initData = getTelegramInitData();
    if (initData) {
      setAuthToken(initData);
    }

    // Load staff member from localStorage
    const savedStaffMember = localStorage.getItem('selectedStaffMember');
    if (savedStaffMember) {
      setStaffMemberState(savedStaffMember);
    }

    // Get Telegram user data
    const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

    if (telegramUser) {
      // WHITELIST: Use shared whitelist from @leafstock/shared
      const userConfig = getWhitelistEntry(telegramUser.id);

      // REJECT: Unauthorized Telegram ID
      if (!userConfig) {
        console.error(`Unauthorized access attempt from Telegram ID: ${telegramUser.id}`);
        setUser(null);
        setBusiness(null);
        setUserBusiness(null);
        setIsLoading(false);

        // Show error to user
        alert(`⛔ Access Denied\n\nYour Telegram account is not authorized to use Leafstock.\n\nTelegram ID: ${telegramUser.id}\n\nPlease contact the administrator.`);
        return;
      }

      // ACCEPT: Create user with assigned role
      setUser({
        id: `tg-${telegramUser.id}`,
        telegramId: String(telegramUser.id),
        firstName: telegramUser.first_name || userConfig.name,
        lastName: telegramUser.last_name || '',
        username: telegramUser.username || '',
        globalRole: userConfig.role as any,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setBusiness({
        id: 'bryce-001',
        code: 'BRYCE001',
        name: 'Bryce Cannabis Co.',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setUserBusiness({
        id: `ub-${telegramUser.id}`,
        userId: `tg-${telegramUser.id}`,
        businessId: 'bryce-001',
        role: userConfig.role as any,
        isActive: true,
        createdAt: new Date(),
      });
    } else if (import.meta.env.DEV) {
      // Fallback for local dev without Telegram
      setUser({
        id: 'dev-user-1',
        telegramId: '7584507876',
        firstName: 'Dev',
        lastName: 'Admin',
        username: 'devadmin',
        globalRole: 'SUPER_ADMIN' as any,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setBusiness({
        id: 'bryce-001',
        code: 'BRYCE001',
        name: 'Bryce Cannabis Co.',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setUserBusiness({
        id: 'dev-ub-1',
        userId: 'dev-user-1',
        businessId: 'bryce-001',
        role: 'SUPER_ADMIN' as any,
        isActive: true,
        createdAt: new Date(),
      });
    }

    setIsLoading(false);
  }, []);

  const setStaffMember = (name: string) => {
    setStaffMemberState(name);
    localStorage.setItem('selectedStaffMember', name);
  };

  const logout = () => {
    setUser(null);
    setBusiness(null);
    setUserBusiness(null);
    setAuthToken(null);
    setStaffMemberState(null);
    localStorage.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        business,
        userBusiness,
        authToken,
        staffMember,
        isAuthenticated: !!user && !!business,
        isLoading,
        setUser,
        setBusiness,
        setUserBusiness,
        setAuthToken,
        setStaffMember,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
